import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaPlus, FaTrash, FaArrowLeft, FaEdit, FaInfoCircle } from "react-icons/fa";
import Hoc from "../../../components/HOC";
import Loader from "../../../components/Loader";
import { RecruiterContext } from "../../../Context/Recruiter";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import moment from "moment";
import AssociateSubmissionModal from "./AssociateSubmissionModal";
import EditSubmissionModal from "./EditSubmissionModal";
import Modal from "../../../components/Modal";
import CustomDropdown from "../../../components/CustomDropdown";
import TextAreaInput from "../../../components/TextArea";
import { Formik, Form } from "formik";
import {
  getRecruitmentSubmissionById,
  updateRecruitmentSubmission,
  getCandidateResumes,
  getSubmissionInfo,
} from "../../../Services/recruiterapis";

const editSubmissionInitialValues = {
  submissionId: null,
  notes: "",
  scheduledDate: "",
  scheduledTime: "",
  status: "",
  rate: "",
  rateType: "",
  stage: "",
};

// Helper function to extract error message from various error formats
const getErrorMessage = (error, defaultMessage = "An error occurred") => {
  if (!error) return defaultMessage;
  
  // Check for response data message (most common for HTTP errors)
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  // Check for response data error field (alternative)
  if (error.response?.data?.error) {
    return typeof error.response.data.error === 'string' 
      ? error.response.data.error 
      : error.response.data.error.message || defaultMessage;
  }
  
  // Check if response data is a string
  if (typeof error.response?.data === 'string') {
    return error.response.data;
  }
  
  // Check for error message (for thrown errors)
  if (error.message) {
    return error.message;
  }
  
  // Fallback to default message
  return defaultMessage;
};

const SubmissionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    getRecruitmentById,
    fetchRecruitmentSubmissions,
    submissions,
    addRecruitmentSubmission,
    removeRecruitmentSubmission,
    fetchCandidates,
    candidates,
    loading: contextLoading,
  } = useContext(RecruiterContext);

  const [requirement, setRequirement] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [formValues, setFormValues] = useState({
    rate: "",
    rateType: "",
    notes: "",
  });
  const [candidateResumes, setCandidateResumes] = useState([]);
  const [resumeAction, setResumeAction] = useState(null);
  const [selectedResumeId, setSelectedResumeId] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeError, setResumeError] = useState(null);
  const [isCheckingResume, setIsCheckingResume] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalValues, setEditModalValues] = useState(editSubmissionInitialValues);
  const [isEditModalLoading, setIsEditModalLoading] = useState(false);
  const [isEditModalSaving, setIsEditModalSaving] = useState(false);
  const [editingSubmissionId, setEditingSubmissionId] = useState(null);
  const [editCandidateResumes, setEditCandidateResumes] = useState([]);
  const [editResumeAction, setEditResumeAction] = useState(null);
  const [editSelectedResumeId, setEditSelectedResumeId] = useState(null);
  const [editResumeFile, setEditResumeFile] = useState(null);
  const [editResumeError, setEditResumeError] = useState(null);
  const [editIsCheckingResume, setEditIsCheckingResume] = useState(false);
  const [editCurrentResume, setEditCurrentResume] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [editIsDragging, setEditIsDragging] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesData, setNotesData] = useState({ notes: "", stage: "" });
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);

  const loadRequirement = async () => {
    try {
      const data = await getRecruitmentById(id);
      const status = data?.stsCd === "A" ? "Active" : "Closed";
      const statusStyles =
        status === "Active"
          ? "bg-emerald-100 text-emerald-700"
          : "bg-red-100 text-red-600";
      
      const normalized = {
        ...data,
        clientName: data?.clientName || "N/A",
        position: data?.position || "N/A",
        requirementDate: data?.requirementDate
          ? moment(data.requirementDate).format("MM/DD/YYYY")
          : "N/A",
        location: data?.location || "N/A",
        rate: data?.rate || "N/A",
        status,
        statusStyles,
      };
      setRequirement(normalized);
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Failed to load requirement details");
      toast.error(errorMessage);
      navigate("/requirements");
    }
  };

  const resetEditResumeState = () => {
    setEditCandidateResumes([]);
    setEditResumeAction(null);
    setEditSelectedResumeId(null);
    setEditResumeFile(null);
    setEditResumeError(null);
    setEditIsCheckingResume(false);
    setEditCurrentResume(null);
    setEditIsDragging(false);
  };

  const loadSubmissions = async (pageParam = page, sizeParam = rowsPerPage, skipLoading = false) => {
    if (!skipLoading) {
      setIsPageLoading(true);
    }
    try {
      const result = await fetchRecruitmentSubmissions(id, {
        page: pageParam - 1,
        size: sizeParam,
      });
      setTotalSubmissions(result?.totalElements || 0);
      setTotalPages(result?.totalPages || 0);
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Failed to load submissions");
      toast.error(errorMessage);
      setTotalSubmissions(0);
      setTotalPages(0);
    } finally {
      if (!skipLoading) {
        setIsPageLoading(false);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;
    
    if (!id) {
      toast.error("Invalid requirement selected");
      navigate("/requirements");
      return;
    }

    setIsPageLoading(true);

    const handler = setTimeout(async () => {
      try {
        await loadRequirement();
        if (!isMounted) return;
        
        const result = await fetchRecruitmentSubmissions(id, {
          page: 0,
          size: rowsPerPage,
        });
        if (!isMounted) return;
        
        setTotalSubmissions(result?.totalElements || 0);
        setTotalPages(result?.totalPages || 0);
        setPage(1);
      } catch (error) {
        if (!isMounted) return;
        const errorMessage = getErrorMessage(error, "Failed to load data");
        toast.error(errorMessage);
        setTotalSubmissions(0);
        setTotalPages(0);
      } finally {
        if (isMounted) {
          setIsPageLoading(false);
        }
      }
    }, 400);

    return () => {
      isMounted = false;
      clearTimeout(handler);
    };
  }, [id, rowsPerPage]);

  const handleChangePage = (newPage) => {
    if (newPage < 1 || newPage === page) return;
    if (totalPages && newPage > totalPages) return;
    setPage(newPage);
    loadSubmissions(newPage, rowsPerPage, false);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    loadSubmissions(1, newRowsPerPage);
  };

  const resetFormState = () => {
    setSelectedCandidate(null);
    setFormValues({ rate: "", rateType: "", notes: "" });
    setCandidateResumes([]);
    setResumeAction(null);
    setSelectedResumeId(null);
    setResumeFile(null);
    setResumeError(null);
    setIsCheckingResume(false);
    setIsDragging(false);
  };

  const handleCloseModal = () => {
    if (isSubmitting) return;
    setShowAddModal(false);
    resetFormState();
  };

  const handleAddButtonClick = async () => {
    resetFormState();
    setIsLoadingCandidates(true);
    try {
      await fetchCandidates();
      setShowAddModal(true);
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Failed to load candidates");
      toast.error(errorMessage);
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  const handleSelectCandidate = async (option) => {
    setSelectedCandidate(option);
    setCandidateResumes([]);
    setSelectedResumeId(null);
    setResumeFile(null);
    setResumeError(null);
    setResumeAction(null);

    if (!option) {
      return;
    }

    setIsCheckingResume(true);
    try {
      const resumes = await getCandidateResumes(option.value);
      setCandidateResumes(resumes);
      if (resumes.length > 0) {
        setResumeAction("USE_EXISTING");
        setSelectedResumeId(resumes[0]?.id || null);
      } else {
        setResumeAction("UPLOAD_NEW");
        toast.info("No resume found for this candidate. Please upload a resume.");
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Failed to check candidate resume");
      toast.error(errorMessage);
      setResumeAction(null);
    } finally {
      setIsCheckingResume(false);
    }
  };

  const handleResumeActionChange = (action) => {
    setResumeAction(action);
    setResumeError(null);

    if (action === "USE_EXISTING" && candidateResumes.length > 0) {
      setSelectedResumeId(candidateResumes[0]?.id || null);
    }

    if (action === "UPLOAD_NEW") {
      setSelectedResumeId(null);
      setResumeFile(null);
    }
  };

  const handleResumeSelection = (resumeId) => {
    setSelectedResumeId(resumeId ? Number(resumeId) : null);
    setResumeError(null);
  };

  const handleResumeFileChange = (file) => {
    setResumeFile(file || null);
    setResumeError(null);
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (selectedCandidate?.value && (resumeAction === "UPLOAD_NEW" || candidateResumes.length === 0)) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    if (!selectedCandidate?.value || (resumeAction !== "UPLOAD_NEW" && candidateResumes.length > 0)) {
      return;
    }

    const files = Array.from(event.dataTransfer.files);
    const validFiles = files.filter((file) => {
      const allowedTypes = [".pdf", ".docx", ".png", ".jpg", ".jpeg", ".bmp", ".gif"];
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();
      
      if (!allowedTypes.includes(fileExtension)) {
        toast.error(`File type ${fileExtension} is not supported. Please use PDF, DOCX, PNG, JPG, JPEG, BMP, or GIF.`);
        return false;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File ${file.name} size should be less than 10MB`);
        return false;
      }

      return true;
    });

    if (validFiles.length > 0) {
      handleResumeFileChange(validFiles[0]);
      if (resumeAction !== "UPLOAD_NEW") {
        handleResumeActionChange("UPLOAD_NEW");
      }
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      const file = files[0];
      const allowedTypes = [".pdf", ".docx", ".png", ".jpg", ".jpeg", ".bmp", ".gif"];
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();
      
      if (!allowedTypes.includes(fileExtension)) {
        toast.error(`File type ${fileExtension} is not supported. Please use PDF, DOCX, PNG, JPG, JPEG, BMP, or GIF.`);
        handleResumeFileChange(null);
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File ${file.name} size should be less than 10MB`);
        handleResumeFileChange(null);
        return;
      }

      // Set resumeAction first to ensure the UI is ready to display the file
      if (resumeAction !== "UPLOAD_NEW") {
        handleResumeActionChange("UPLOAD_NEW");
      }
      // Then set the file
      handleResumeFileChange(file);
    }
  };

  const handleDeleteFile = () => {
    handleResumeFileChange(null);
  };

  const handleEditResumeActionChange = (action) => {
    setEditResumeAction(action);
    setEditResumeError(null);
    if (action === "USE_EXISTING" && !editSelectedResumeId) {
      const firstResume = editCandidateResumes?.[0];
      if (firstResume?.id) {
        setEditSelectedResumeId(firstResume.id);
      }
    }

    if (action !== "UPLOAD_NEW") {
      setEditResumeFile(null);
    }
  };

  const handleEditResumeSelection = (resumeId) => {
    setEditSelectedResumeId(resumeId ? Number(resumeId) : null);
    setEditResumeError(null);
  };

  const handleEditResumeFileChange = (file) => {
    setEditResumeFile(file || null);
    setEditResumeError(null);
  };

  const handleEditDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (editResumeAction === "UPLOAD_NEW") {
      setEditIsDragging(true);
    }
  };

  const handleEditDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setEditIsDragging(false);
  };

  const handleEditDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setEditIsDragging(false);

    if (editResumeAction !== "UPLOAD_NEW") {
      return;
    }

    const files = Array.from(event.dataTransfer.files);
    const validFiles = files.filter((file) => {
      const allowedTypes = [".pdf", ".docx", ".png", ".jpg", ".jpeg", ".bmp", ".gif"];
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();
      
      if (!allowedTypes.includes(fileExtension)) {
        toast.error(`File type ${fileExtension} is not supported. Please use PDF, DOCX, PNG, JPG, JPEG, BMP, or GIF.`);
        return false;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File ${file.name} size should be less than 10MB`);
        return false;
      }

      return true;
    });

    if (validFiles.length > 0) {
      handleEditResumeFileChange(validFiles[0]);
      if (editResumeAction !== "UPLOAD_NEW") {
        handleEditResumeActionChange("UPLOAD_NEW");
      }
    }
  };

  const handleEditFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      const file = files[0];
      const allowedTypes = [".pdf", ".docx", ".png", ".jpg", ".jpeg", ".bmp", ".gif"];
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();
      
      if (!allowedTypes.includes(fileExtension)) {
        toast.error(`File type ${fileExtension} is not supported. Please use PDF, DOCX, PNG, JPG, JPEG, BMP, or GIF.`);
        handleEditResumeFileChange(null);
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File ${file.name} size should be less than 10MB`);
        handleEditResumeFileChange(null);
        return;
      }

      handleEditResumeFileChange(file);
      if (editResumeAction !== "UPLOAD_NEW") {
        handleEditResumeActionChange("UPLOAD_NEW");
      }
    }
  };

  const handleEditDeleteFile = () => {
    handleEditResumeFileChange(null);
  };

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error("File missing"));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () =>
        reject(new Error("Failed to read resume file. Please try again."));
      reader.readAsDataURL(file);
    });

  const handleAssociateSubmission = async (formValuesFromFormik) => {
    if (!selectedCandidate) {
      toast.warn("Please select a candidate to associate.");
      return;
    }

    if (!resumeAction || resumeAction === "NONE") {
      setResumeError("Please choose how you want to handle the resume.");
      toast.warn("Select a resume option before submitting.");
      return;
    }

    if (resumeAction === "USE_EXISTING" && !selectedResumeId) {
      setResumeError("Select an existing resume to continue.");
      toast.warn("Please choose which resume to use.");
      return;
    }

    if (resumeAction === "UPLOAD_NEW" && !resumeFile) {
      setResumeError("Upload a resume to continue.");
      toast.warn("Please upload a resume file.");
      return;
    }

    const sanitize = (value) => {
      if (typeof value !== "string") return null;
      const trimmed = value.trim();
      return trimmed.length ? trimmed : null;
    };

    // Use form values from Formik if provided, otherwise fall back to state
    const valuesToUse = formValuesFromFormik || formValues;

    try {
      setResumeError(null);
      setIsSubmitting(true);

      const payload = {
        submissionId: selectedCandidate.value,
        rate: sanitize(valuesToUse.rate),
        rateType: sanitize(valuesToUse.rateType),
        notes: sanitize(valuesToUse.notes),
        resumeAction,
        candidateDocumentId:
          resumeAction === "USE_EXISTING" ? selectedResumeId : null,
      };

      if (resumeAction === "UPLOAD_NEW") {
        const base64Data = await readFileAsBase64(resumeFile);
        payload.newResume = {
          documentType: "RESUME",
          fileName: resumeFile.name,
          fileType: resumeFile.type || "application/pdf",
          base64Data,
        };
      }

      await addRecruitmentSubmission(id, {
        ...payload,
      });
      toast.success("Submission associated successfully!");
      setShowAddModal(false);
      resetFormState();
      setPage(1);
      // Keep isSubmitting true while refreshing table
      await loadSubmissions(1, rowsPerPage, true);
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Failed to associate submission");
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeleteSubmission = (submissionId, candidateName) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Deactivate association for ${candidateName || "this submission"}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, deactivate it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsPageLoading(true);
          await removeRecruitmentSubmission(id, submissionId);
          Swal.fire({
            title: "Deactivated!",
            text: "Association deactivated successfully.",
            icon: "success",
          });
          await loadSubmissions(page, rowsPerPage, false);
        } catch (error) {
          const errorMessage = getErrorMessage(error, "Failed to deactivate association");
          Swal.fire({
            title: "Error!",
            text: errorMessage,
            icon: "error",
          });
        } finally {
          setIsPageLoading(false);
        }
      }
    });
  };

  const handleOpenEditSubmission = async (submission) => {
    if (!submission?.id) {
      toast.error("Invalid submission selected");
      return;
    }
    resetEditResumeState();
    setEditingSubmissionId(submission.id);
    setShowEditModal(true);
    setIsEditModalLoading(true);
    
    // Load submission details first
    try {
      const detail = await getRecruitmentSubmissionById(id, submission.id);
      const scheduledDate = detail?.scheduledDate
        ? moment(detail.scheduledDate).format("YYYY-MM-DD")
        : "";
      const scheduledTimeRaw = detail?.scheduledTime || "";
      const scheduledTime = scheduledTimeRaw
        ? moment(scheduledTimeRaw, ["HH:mm:ss", "HH:mm"]).format("HH:mm")
        : "";

      setEditModalValues({
        submissionId: detail?.submissionId || submission.id,
        notes: detail?.notes || "",
        scheduledDate,
        scheduledTime,
        status: detail?.status || submission.status || "",
        rate: detail?.rate || submission.rate || "",
        rateType: detail?.rateType || submission.rateType || "",
        stage: detail?.stage || submission.stage || "",
      });
      const candidateDocId = detail?.resume?.sourceCandidateDocumentId || null;
      setEditCurrentResume(detail?.resume || null);
      setEditSelectedResumeId(candidateDocId);
      setEditResumeAction(null);
      setEditResumeFile(null);
      setEditResumeError(null);

      // Display modal immediately after submission details are loaded
      setIsEditModalLoading(false);

      // Load candidate resumes asynchronously in the background
      setEditIsCheckingResume(true);
      getCandidateResumes(detail?.submissionId || submission.id)
        .then((resumes) => {
          setEditCandidateResumes(resumes || []);
          
          // Validate if the current resume ID still exists in available resumes
          if (candidateDocId) {
            const resumeExists = resumes?.some(resume => resume.id === candidateDocId);
            if (!resumeExists) {
              // The previously linked resume was deleted, clear the selection
              setEditSelectedResumeId(null);
              if (resumes?.length > 0) {
                // Auto-select the first available resume
                setEditSelectedResumeId(resumes[0].id);
                toast.info("The previously linked resume was deleted. A new resume has been auto-selected.");
              } else {
                toast.warn("The previously linked resume was deleted. Please upload a new resume.");
              }
            }
          } else if (resumes?.length > 0) {
            // No previous resume linked, select the first available
            setEditSelectedResumeId(resumes[0].id);
          }
        })
        .catch((resumeError) => {
          // Silently handle resume loading errors - show error only if it's a critical issue
          const errorMessage = getErrorMessage(resumeError, "");
          if (errorMessage && !errorMessage.includes("No documents found") && !errorMessage.includes("No resume documents found")) {
            toast.error(errorMessage);
          }
          setEditCandidateResumes([]);
          // Clear selected resume if loading failed
          setEditSelectedResumeId(null);
        })
        .finally(() => {
          setEditIsCheckingResume(false);
      });
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Failed to load submission details");
      toast.error(errorMessage);
      setShowEditModal(false);
      setEditingSubmissionId(null);
      setIsEditModalLoading(false);
    }
  };

  const handleSaveEditSubmission = async (values) => {
    if (!editingSubmissionId) {
      toast.error("Submission identifier missing");
      return;
    }
    setEditResumeError(null);
    if (editResumeAction === "USE_EXISTING") {
      if (!editSelectedResumeId) {
        setEditResumeError("Select an existing resume to continue.");
        toast.warn("Select an existing resume to continue.");
        return;
      }
      // Validate that the selected resume ID exists in available resumes
      const resumeExists = editCandidateResumes?.some(resume => resume.id === editSelectedResumeId);
      if (!resumeExists) {
        setEditResumeError("The selected resume no longer exists. Please select a different resume or upload a new one.");
        toast.error("The selected resume was deleted. Please select a different resume or upload a new one.");
        return;
      }
    }

    if (editResumeAction === "UPLOAD_NEW" && !editResumeFile) {
      setEditResumeError("Upload a resume to continue.");
      toast.warn("Upload a resume to continue.");
      return;
    }

    let uploadPayload = null;
    if (editResumeAction === "UPLOAD_NEW" && editResumeFile) {
      try {
        const base64Data = await readFileAsBase64(editResumeFile);
        uploadPayload = {
          documentType: "RESUME",
          fileName: editResumeFile.name,
          fileType: editResumeFile.type || "application/pdf",
          base64Data,
        };
      } catch (fileError) {
        const message = getErrorMessage(fileError, "Failed to process the resume file.");
        setEditResumeError(message);
        toast.error(message);
        return;
      }
    }

    const payload = {
      submissionId: values.submissionId || editingSubmissionId,
      notes: values.notes?.trim() || null,
      scheduledDate: values.scheduledDate || null,
      scheduledTime: values.scheduledTime || null,
      status: values.status?.trim() || null,
      rate: values.rate?.trim() || null,
      rateType: values.rateType?.trim() || null,
      stage: values.stage?.trim() || null,
    };

    if (editResumeAction) {
      payload.resumeAction = editResumeAction;
      payload.candidateDocumentId =
        editResumeAction === "USE_EXISTING" ? editSelectedResumeId : null;
      if (editResumeAction === "UPLOAD_NEW" && uploadPayload) {
        payload.newResume = uploadPayload;
      }
    }

    setIsEditModalSaving(true);
    try {
      await updateRecruitmentSubmission(id, payload);
      toast.success("Submission updated successfully!");
      setShowEditModal(false);
      setEditingSubmissionId(null);
      setEditModalValues(editSubmissionInitialValues);
      resetEditResumeState();
      setPage(1);
      // Keep isEditModalSaving true while refreshing table
      await loadSubmissions(1, rowsPerPage, true);
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Failed to update submission");
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsEditModalSaving(false);
    }
  };

  const handleCloseEditModal = () => {
    if (isEditModalSaving) return;
    setShowEditModal(false);
    setEditingSubmissionId(null);
    setEditModalValues(editSubmissionInitialValues);
    resetEditResumeState();
  };

  const handleOpenNotesModal = async (submission) => {
    if (!submission?.id) {
      toast.error("Invalid submission selected");
      return;
    }
    setSelectedSubmissionId(submission.id);
    setShowNotesModal(true);
    setIsLoadingNotes(true);
    setNotesData({ notes: "", stage: "" });
    
    try {
      const info = await getSubmissionInfo(id, submission.id);
      setNotesData({
        notes: info?.notes || "",
        stage: info?.stage || "",
      });
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Failed to load notes");
      toast.error(errorMessage);
      setShowNotesModal(false);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  const handleCloseNotesModal = () => {
    setShowNotesModal(false);
    setNotesData({ notes: "", stage: "" });
    setSelectedSubmissionId(null);
  };

  return (
    <>
      <div className="bg-white">
      <Loader isLoading={isPageLoading || contextLoading || isSubmitting || isEditModalSaving || isLoadingCandidates || isEditModalLoading} />
      <div className="p-6 w-full bg-white h-full">
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="flex flex-row flex-wrap items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => navigate("/requirements")}
                className="inline-flex h-10 w-fit shrink-0 items-center justify-center rounded-[4px] border border-[#D1D1D1] bg-white px-4 text-sm font-medium text-[#201F1E] transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#201F1E]/20 focus:ring-offset-1"
              >
                <FaArrowLeft className="mr-2" size={16} />
                Back
              </button>
              <h2 className="text-xl font-semibold text-gray-900">Requirement Overview</h2>
            </div>
            {requirement && (
              <span className={`px-3 py-1 text-xs font-semibold rounded-full shrink-0 ${requirement.statusStyles || "bg-gray-100 text-gray-600"}`}>
                {requirement.status || "N/A"}
              </span>
            )}
          </div>

          {requirement ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { label: "Client", value: requirement.clientName || "N/A" },
                { label: "Position", value: requirement.position || "N/A" },
                { label: "Location", value: requirement.location || "N/A" },
                { label: "Rate", value: requirement.rate || "N/A" },
                { label: "Posted Date", value: requirement.requirementDate || "N/A" },
                { label: "Source", value: requirement.source || "N/A" },
                { label: "Status", value: requirement.status || "N/A" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-xs uppercase tracking-wide text-[#656464] whitespace-nowrap">
                      {item.label}
                    </span>
                    <span className="text-sm font-semibold text-[#201F1E] text-right break-words">
                      {item.value}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Loading requirement details...</p>
          )}
        </div>

        <div className="overflow-hidden bg-white border border-gray-200 rounded-lg">
          <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Submissions</h3>
            </div>
            <button
              onClick={handleAddButtonClick}
              className="px-4 py-2 bg-[#201F1E] text-white rounded-lg hover:bg-[#201F1E] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#201F1E] flex items-center self-start sm:self-auto"
              disabled={isLoadingCandidates}
            >
              {isLoadingCandidates ? (
                "Loading..."
              ) : (
                <>
                  <FaPlus className="mr-2" /> Add Submission
                </>
              )}
            </button>
          </div>

          <div className="overflow-x-auto">
            <div className="overflow-y-auto" style={{ maxHeight: "60vh" }}>
              <table className="min-w-full divide-y divide-gray-200 table-fixed">
                <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="w-[180px] px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider">Candidate</th>
                  <th className="w-[220px] px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider">Email</th>
                  <th className="w-[140px] px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider">Phone</th>
                  <th className="w-[120px] px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider">Rate</th>
                  <th className="w-[140px] px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider whitespace-nowrap">Rate Type</th>
                  {/* <th className="w-[140px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Stage</th> */}
                  <th className="w-[160px] px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider whitespace-nowrap">Scheduled Date</th>
                  <th className="w-[140px] px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider whitespace-nowrap">Scheduled Time</th>
                  <th className="w-[140px] px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider whitespace-nowrap">Status</th>
                  <th className="w-[120px] px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider">Actions</th>
                </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {submissions.length === 0 && !isPageLoading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-sm text-gray-500">
                      No submissions associated yet.
                    </td>
                  </tr>
                ) : (
                  submissions.map((submission) => (
                  <tr key={submission.id}>
                      <td className="px-6 py-4 text-sm text-[#656464]">
                        <div className="max-w-[180px] truncate">{submission.firstName + " " + submission.lastName || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#656464]">
                        <div className="max-w-[220px] truncate">{submission.email || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#656464]">
                        <div className="max-w-[140px] truncate">{submission.phone || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#656464]">
                        <div className="max-w-[120px] truncate">{submission.rate || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#656464]">
                        <div className="max-w-[140px] truncate">{submission.rateType || "N/A"}</div>
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap">
                       
                        <div className="max-w-[140px] truncate">{submission.stage || "N/A"}</div>
                        
                      </td> */}
                      {/* <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="max-w-[140px] truncate">{submission.status || "N/A"}</div>
                      </td> */}
                      <td className="px-6 py-4 text-sm text-[#656464]">
                        <div className="max-w-[160px] truncate">
                          {submission.scheduledDate ? moment(submission.scheduledDate).format("MM/DD/YYYY") : "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#656464]">
                        <div className="max-w-[140px] truncate">{submission.scheduledTime || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            submission.status === "D"
                              ? "bg-red-50 text-red-400"
                              : "bg-[#34D39920] text-[#34D399]"
                          }`}
                        >
                          {submission.status === "D" ? "Inactive" : "Active"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => handleOpenNotesModal(submission)}
                            className="text-green-600 hover:text-green-800"
                            title="View Notes"
                          >
                            <FaInfoCircle size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOpenEditSubmission(submission)}
                            disabled={submission.status === "D"}
                            className={`${submission.status === "D" ? "text-gray-400 cursor-not-allowed" : "text-[#201F1E] hover:text-[#201F1E]"}`}
                            title={submission.status === "D" ? "Cannot edit inactive submission" : "Edit Interview"}
                          >
                            <FaEdit size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteSubmission(submission.id, submission.candidateName)}
                            disabled={submission.status === "D"}
                            className={`${submission.status === "D" ? "text-gray-400 cursor-not-allowed" : "text-red-600 hover:text-red-800"}`}
                            title={submission.status === "D" ? "Cannot delete inactive submission" : "Remove Association"}
                          >
                            <FaTrash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => page > 1 && handleChangePage(1)}
                disabled={page === 1 || totalSubmissions === 0}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                onClick={() => page > 1 && handleChangePage(page - 1)}
                disabled={page === 1 || totalSubmissions === 0}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => page < totalPages && handleChangePage(page + 1)}
                disabled={page === totalPages || totalSubmissions === 0}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => page < totalPages && handleChangePage(totalPages)}
                disabled={page === totalPages || totalSubmissions === 0}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{totalSubmissions === 0 ? 0 : (page - 1) * rowsPerPage + 1}</span> to <span className="font-medium">{Math.min(page * rowsPerPage, totalSubmissions)}</span> of <span className="font-medium">{totalSubmissions}</span> submissions
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label htmlFor="rows-per-page" className="text-sm text-gray-700">
                    Rows per page:
                  </label>
                  <select
                    id="rows-per-page"
                    value={rowsPerPage}
                    onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => page > 1 && handleChangePage(1)}
                    disabled={page === 1 || totalSubmissions === 0}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    First
                  </button>
                  <button
                    onClick={() => page > 1 && handleChangePage(page - 1)}
                    disabled={page === 1 || totalSubmissions === 0}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {[page - 2, page - 1, page, page + 1, page + 2]
                    .filter((p) => p >= 1 && p <= totalPages)
                    .map((pageNumber) => (
                      <button
                        key={pageNumber}
                        onClick={() => handleChangePage(pageNumber)}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 ${
                          pageNumber === page
                            ? "bg-blue-50 text-blue-600"
                            : "bg-white text-gray-700"
                        } text-sm font-medium hover:bg-gray-50`}
                      >
                        {pageNumber}
                      </button>
                    ))}
                  <button
                    onClick={() => page < totalPages && handleChangePage(page + 1)}
                    disabled={page === totalPages || totalSubmissions === 0}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => page < totalPages && handleChangePage(totalPages)}
                    disabled={page === totalPages || totalSubmissions === 0}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Last
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      <AssociateSubmissionModal
        show={showAddModal}
        onClose={handleCloseModal}
        isLoadingCandidates={isLoadingCandidates}
        candidates={candidates}
        selectedCandidate={selectedCandidate}
        onSelectCandidate={handleSelectCandidate}
        formValues={formValues}
        onFieldChange={handleFieldChange}
        onSubmit={handleAssociateSubmission}
        isSubmitting={isSubmitting}
        candidateResumes={candidateResumes}
        resumeAction={resumeAction}
        onResumeActionChange={handleResumeActionChange}
        selectedResumeId={selectedResumeId}
        onSelectResume={handleResumeSelection}
        onResumeFileChange={handleResumeFileChange}
        resumeFile={resumeFile}
        isCheckingResume={isCheckingResume}
        resumeError={resumeError}
        onFileChange={handleFileChange}
        onDeleteFile={handleDeleteFile}
      />

      <EditSubmissionModal
        show={showEditModal}
        onClose={handleCloseEditModal}
        initialValues={editModalValues}
        isLoading={isEditModalLoading}
        isSaving={isEditModalSaving}
        onSubmit={handleSaveEditSubmission}
        candidateResumes={editCandidateResumes}
        resumeAction={editResumeAction}
        onResumeActionChange={handleEditResumeActionChange}
        selectedResumeId={editSelectedResumeId}
        onSelectResume={handleEditResumeSelection}
        resumeFile={editResumeFile}
        onResumeFileChange={handleEditResumeFileChange}
        resumeError={editResumeError}
        currentResume={editCurrentResume}
        isCheckingResume={editIsCheckingResume}
        isDragging={editIsDragging}
        onDragOver={handleEditDragOver}
        onDragLeave={handleEditDragLeave}
        onDrop={handleEditDrop}
        onFileChange={handleEditFileChange}
        onDeleteFile={handleEditDeleteFile}
      />

      <Modal
        show={showNotesModal}
        onClose={handleCloseNotesModal}
        title="Submission Information"
        classes="w-[80vw] max-w-[90vw]"
      >
        {isLoadingNotes ? (
          <div className="flex flex-col justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
            <div className="text-gray-500 text-sm">Loading information...</div>
          </div>
        ) : (
          <Formik>
            <Form className="mt-6 space-y-5">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">Notes</label>
                <TextAreaInput
                  name="notes"
                  value={notesData.notes || ""}
                  handleChange={() => {}}
                  isDisabled={true}
                  rows={8}
                  classes="w-full"
                />
                {!notesData.notes && (
                  <p className="text-xs text-gray-400 mt-1 italic">No notes have been added yet</p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">Stage</label>
                <TextAreaInput
                  name="stage"
                  value={notesData.stage || ""}
                  handleChange={() => {}}
                  isDisabled={true}
                  rows={8}
                  classes="w-full"
                />
                {!notesData.stage && (
                  <p className="text-xs text-gray-400 mt-1 italic">No stage information available</p>
                )}
              </div>
            </Form>
          </Formik>
        )}
      </Modal>
    </>
  );
};

export default Hoc(SubmissionPage);

